import React, { forwardRef } from 'react';
import { StackItem } from '#components/stack-layout/stack-item/stack-item.tsx';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import {
  GDSHeadingLevel,
  HeadingText,
} from '#components/typography/heading-text/heading-text.tsx';
import { ParagraphText } from '#components/typography/paragraph-text/paragraph-text.tsx';
import { UtilityText } from '#components/typography/utility-text/utility-text.tsx';
import { Image } from '#components/image/image.tsx';
import { GDSCustomizableComponent, GDSTrailingIcon } from '#src/types.ts';
import { Icon } from '#components/icon/icon.tsx';
import { useThemeStrings } from '#hooks/use-theme.ts';
import { GDSTagProps, Tag } from '#components/tag/tag.tsx';
import { ConditionalLinkRenderer } from './conditional-link-or-button';

const DISPLAY_NAME = 'ComposableCardBody';

export interface GDSComposableCardBodyContentProps
  extends GDSCustomizableComponent {
  linkAs?: React.ElementType;
  caption?: string;
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  href?: string;
  imageAltText?: string;
  imagePosition?: 'leading' | 'trailing';
  imageSize?: string;
  imageSrc?: string;
  imageLoading?: 'lazy' | 'eager';
  onClick?: (event: React.MouseEvent) => void;
  subheading?: string;
  trailingIndicator?: GDSTrailingIcon;
  tags?: GDSTagProps[];
  tagsPosition?: 'top' | 'bottom';
  trailingIndicatorAltText?: string;
}

const indicatorByString = {
  externalLink: 'tinyExternalLink',
  meatballs: 'interfaceMeatballs',
  newWindowInternal: 'tinyNewWindowInternal',
  rightChevron: 'interfaceChevronForward',
};

const getIconByIndicator = (indicator: GDSTrailingIcon) =>
  indicatorByString[indicator];

const ComposableCardTags = ({ tags }: { tags?: GDSTagProps[] }) => {
  if (!tags?.some((tag) => tag.label?.trim())) {
    return null;
  }
  const tagsWithLabels = tags.filter((tag) => tag.label?.trim());
  return (
    <StackLayout
      orientation="horizontal"
      spacing="$half"
      css={{ flexWrap: 'wrap' }}
    >
      {tagsWithLabels.map((tag) => (
        <StackItem growFactor={0} key={tag.label}>
          <Tag {...tag} />
        </StackItem>
      ))}
    </StackLayout>
  );
};

export const ComposableCardBodyContent = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  GDSComposableCardBodyContentProps
>(
  (
    {
      caption,
      className,
      css,
      heading,
      headingLevel = '3',
      href,
      imageAltText,
      imagePosition = 'leading',
      imageSize = '48px',
      imageSrc,
      imageLoading = 'lazy',
      onClick,
      subheading,
      trailingIndicator,
      trailingIndicatorAltText,
      linkAs,
      tags,
      tagsPosition = 'top',
      // Need to get extra props which could be passed for the linkAs
      ...props
    },
    ref,
  ) => {
    const stringsMap = useThemeStrings();

    const labelByIndicator = {
      externalLink: stringsMap.externalLink,
      meatballs: stringsMap.moreOptions,
      newWindowInternal: stringsMap.newWindowInternal,
      rightChevron: stringsMap.disclosure,
    };

    const getLabelByIndicator = (indicatorIcon: GDSTrailingIcon) =>
      labelByIndicator[indicatorIcon];

    return (
      <StackLayout
        className={['GDS-composable-card-body-content', className].join(' ')}
        css={{ flexWrap: 'wrap', flex: 0, ...css }}
        data-testid={`GDS-${DISPLAY_NAME}`}
        horizontalAlignment="spaceBetween"
        orientation="vertical"
        spacing="$half"
      >
        {tagsPosition === 'top' && <ComposableCardTags tags={tags} />}
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="spaceBetween"
          verticalAlignment="center"
          spacing="$half"
        >
          {imageSrc && imagePosition === 'leading' && (
            <Image
              src={imageSrc}
              alt={imageAltText}
              className="GDS-composable-card-body-image"
              css={{ width: imageSize, height: imageSize }}
              loading={imageLoading}
            />
          )}
          {(caption || heading || subheading) && (
            <StackItem css={{ flex: 1 }}>
              {caption && (
                <UtilityText size="xs" emphasis="base">
                  {caption}
                </UtilityText>
              )}
              {heading && (
                <HeadingText
                  level={headingLevel}
                  size="sm"
                  css={{
                    '& [class*="GDS"]:focus': { outlineOffset: '0' },
                    '.GDS-composable-card-action': {
                      color: 'inherit',
                      font: 'inherit',
                    },
                  }}
                >
                  <ConditionalLinkRenderer
                    heading={heading}
                    href={href}
                    linkAs={linkAs}
                    onClick={onClick}
                    ref={ref}
                    {...props}
                  />
                </HeadingText>
              )}
              {subheading && <ParagraphText>{subheading}</ParagraphText>}
            </StackItem>
          )}
          {trailingIndicator && (
            <Icon
              icon={getIconByIndicator(trailingIndicator)}
              label={
                trailingIndicatorAltText ||
                getLabelByIndicator(trailingIndicator)
              }
            />
          )}
          {imageSrc && imagePosition === 'trailing' && !trailingIndicator && (
            <Image
              alt={imageAltText}
              className="GDS-composable-card-body-image"
              css={{ width: imageSize, height: imageSize }}
              src={imageSrc}
              loading={imageLoading}
            />
          )}
        </StackLayout>
        {tagsPosition === 'bottom' && <ComposableCardTags tags={tags} />}
      </StackLayout>
    );
  },
);

ComposableCardBodyContent.displayName = DISPLAY_NAME;
